<template>
  <!-- 辅料列表 -->
  <div class="material-page">
    <baseBreadcrumb :page="$t('message.materialList')" :menu="$t('message.warehouse')"></baseBreadcrumb>
    <div class="body">
      <!-- 搜索条件 -->
      <div class="search-area">
        <!-- 商品管理番号 -->
        <div class="item">
          <el-input v-model="searchForm.sorting" :placeholder="$t('message.productManagementNumber')" clearable />
        </div>
        <!-- 客户名/ID -->
        <div class="item">
          <el-input v-model="searchForm.user_id" :placeholder="$t('message.customerNameOrID')" clearable />
        </div>
        <!-- 辅料名称 -->
        <div class="item">
          <el-input v-model="searchForm.name" :placeholder="$t('message.materialName')" clearable />
        </div>
        <!-- 订单号 -->
        <div class="item">
          <el-input v-model.trim="searchForm.order_sn" :placeholder="$t('message.orderNumber')" clearable
            @keyup.enter.native="search" />
        </div>
        <!-- 辅料类型 -->
        <div class="item">
          <el-select v-model="searchForm.type_id" :placeholder="$t('message.materialType')" clearable filterable>
            <el-option v-for="item in typeList" :key="item.id" :label="item.name" :value="item.id" />
          </el-select>
        </div>
        <!-- 搜索和重置 -->
        <div class="search-buttons">
          <el-button type="primary" @click="search">{{ $t('message.search') }}</el-button>
          <el-button @click="reset">{{ $t('message.reset') }}</el-button>
        </div>
        <!-- 操作按钮 -->
        <div class="action-buttons">
          <el-button type="success" @click="materialTypeSetting">{{ $t('message.materialTypeSetting') }}</el-button>
          <el-button type="success" @click="materialAdd">{{ $t('message.materialAdd') }}</el-button>
          <div class="view-switch">
            <el-button size="small" icon="el-icon-s-unfold" :class="{ active: viewMode == 'list' }"
              @click="viewMode = 'list'"></el-button>
            <el-button size="small" icon="el-icon-menu" :class="{ active: viewMode == 'card' }"
              @click="viewMode = 'card'"></el-button>
          </div>
        </div>
      </div>

      <!-- 列表视图：按行展示辅料库存、预计消耗和订单绑定信息 -->
      <div v-if="viewMode == 'list'" class="material-list" v-loading="loading">
        <!-- 列表表头 -->
        <div class="list-head">
          <div>客户信息</div>
          <div>{{ $t('message.materialId') }}</div>
          <div>{{ $t('message.materialDetail') }}</div>
          <div class="actual-stock-cell">{{ $t('message.actualStockQuantity') }}</div>
          <div>{{ $t('message.expectedConsumptionQuantity') }}</div>
          <div>{{ $t('message.expectedReplenishmentQuantity') }}</div>
          <div>{{ $t('message.expectedConsumption') }}</div>
          <div>{{ $t('message.status') }}</div>
          <div>{{ $t('message.materialInOrder') }}</div>
          <div>{{ $t('message.operation') }}</div>
        </div>
        <!-- 无数据占位 -->
        <div v-if="!tableData.length && !loading" class="list-empty">-</div>
        <!-- 辅料列表行 -->
        <div v-for="item in tableData" :key="item.id" class="list-row">
          <!-- 客户信息 -->
          <div class="client-cell">
            <p>ID:{{ item.user_id || '-' }}</p>
            <p>{{ getUserName(item) }}</p>
          </div>
          <!-- 辅料ID -->
          <div class="id-cell">{{ item.id || '-' }}</div>
          <!-- 辅料详情：图片、名称、类型 -->
          <div class="material-cell">
            <div class="material-thumb">
              <el-image v-if="item.main_image" :src="item.main_image" fit="cover" style="width:100%;height:100%"
                :preview-src-list="[item.main_image]" />
            </div>
            <div class="material-text">
              <div class="material-title">{{ item.name_trans || '-' }}</div>
              <el-tag size="mini" type="primary" effect="plain">{{ getMaterialTypeName(item) }}</el-tag>
            </div>
          </div>
          <!-- 实际库存数量 -->
          <div class="num-cell actual-stock-cell">{{ formatNum(item.material_stock_num) }}</div>
          <!-- 预计消耗数量 -->
          <div class="num-cell">
            {{ formatNum(item.pre_consume_num) }}
            <el-button type="text" class="inline-detail" @click="openBindOrderUsage(item)">{{
              $t('message.theDetail') }}</el-button>
          </div>
          <!-- 预计补货数量 -->
          <div class="num-cell">{{ formatNum(item.pre_up_num) }}</div>
          <!-- 预计消耗点数 -->
          <div class="num-cell">{{ item.consume || 0 }}{{ $t('message.pointsPerSet') }}</div>
          <!-- 库存状态 -->
          <div class="status-cell">
            <span class="stock-status" :class="stockStatusClass(item)">{{ stockStatusText(item) }}</span>
          </div>
          <!-- 辅料所在订单 -->
          <div class="orders-cell">
            <div v-for="(order, orderIndex) in getOrderList(item)" :key="orderIndex" class="order-card">
              <router-link :to="`/orderPaymentDetail?order_sn=${order.order_sn}`" target="_blank" class="order-sn">
                {{ order.order_sn }}
              </router-link>
              <div>
                <span>{{ order.sorting }}{{ $t('message.mash') }}</span>
                <span class="order-points">
                  {{ $t('message.remainingPoints') }}
                  <b>{{ order.send_await_num }}</b>
                  {{ $t('message.point') }}
                </span>
              </div>
            </div>
            <div v-if="!getOrderList(item).length" class="order-empty">-</div>
          </div>
          <!-- 操作按钮：沿用原有详情、编辑、删除、订单绑定功能 -->
          <div class="operation-cell">
            <el-button type="primary" size="small" @click="detail(item)">{{ $t('message.detail') }}</el-button>
            <el-button class="btn-green" size="small" @click="edit(item)">{{ $t('message.materialEdit') }}</el-button>
            <el-button class="btn-dark" size="small" @click="deleteItem(item)">{{ $t('message.delete') }}</el-button>
            <el-button v-if="isAuth(670)" class="btn-blue" size="small" @click="addOrder(item)">订单绑定</el-button>
          </div>
        </div>
      </div>

      <!-- 卡片视图：保留原展示和原功能 -->
      <div v-else class="card-list" v-loading="loading">
        <div v-for="item in tableData" :key="item.id" class="material-card">
          <!-- 卡片主体 -->
          <div class="card-top">
            <!-- 头部：客户信息 + 辅料ID -->
            <div class="card-header">
              <div class="header-line">
                <span class="hl-text">{{ $t('message.clientID') }}：{{ item.user_id || '-' }}</span>
                <span class="hl-text">{{ (item.user && item.user.realname) || '-' }}</span>
                <!-- 删除辅料 -->
                <i class="el-icon-delete header-icon" @click="deleteItem(item)"></i>
              </div>
              <div class="header-line">
                <span class="hl-text">{{ $t('message.materialId') }}：{{ item.id || '-' }}</span>
              </div>
            </div>

            <!-- 主信息：图片 + 详情 -->
            <div class="card-main">
              <!-- 辅料图片 -->
              <div class="material-img">
                <el-image v-if="item.main_image" :src="item.main_image" fit="cover" style="width:100%;height:100%"
                  :preview-src-list="[item.main_image]" />
              </div>
              <!-- 辅料详情 -->
              <div class="material-detail">
                <!-- 类型标签 + 名称 -->
                <div class="detail-title">
                  <span class="type-tag">{{ getMaterialTypeName(item) }}</span>
                  <span class="material-name">{{ item.name_trans || '-' }}</span>
                </div>
                <!-- 状态及数量 -->
                <div class="detail-info">
                  <div class="info-row">
                    <span class="label">{{ $t('message.status') }}：</span>
                    <span class="status" :class="{ insufficient: isOutOfStock(item) || isStockInsufficient(item) }">
                      {{ isOutOfStock(item) ? $t('message.noStock') : isStockInsufficient(item) ?
                        $t('message.stockInsufficient') : $t('message.stockSufficient') }}
                    </span>
                  </div>
                  <!-- 实际库存数量 -->
                  <div class="info-row">
                    <span class="label">{{ $t('message.actualStockQuantity') }}：</span>
                    <span class="value value-stock">{{ formatNum(item.material_stock_num) }}</span>
                  </div>
                  <!-- 预计消耗数量 -->
                  <div class="info-row">
                    <span class="label">{{ $t('message.expectedConsumptionQuantity') }}：</span>
                    <span class="value value-consume">{{ formatNum(item.pre_consume_num) }}</span>
                    <el-button type="text" class="detail-btn" @click="openBindOrderUsage(item)">{{
                      $t('message.theDetail') }}</el-button>
                  </div>
                  <!-- 预计补货数量 -->
                  <div class="info-row">
                    <span class="label">{{ $t('message.expectedReplenishmentQuantity') }}：</span>
                    <span class="value value-replenish">{{ formatNum(item.pre_up_num) }}</span>
                  </div>
                  <!-- 消耗点数 -->
                  <div class="info-row">
                    <span class="label">{{ $t('message.expectedConsumption') }}：</span>
                    <span class="value">{{ item.consume || 0 }} {{ $t('message.pointsPerSet') }}</span>
                  </div>
                </div>
                <!-- 操作按钮 -->
                <div class="detail-btns">
                  <el-button type="primary" size="small" @click="detail(item)">{{ $t('message.detail') }}</el-button>
                  <el-button class="btn-green" size="small" @click="edit(item)">{{ $t('message.materialEdit')
                  }}</el-button>
                </div>
              </div>
            </div>
          </div>

          <!-- 分割线 -->
          <div class="card-divider"></div>

          <!-- 辅料所在订单 -->
          <div class="card-orders">
            <div class="orders-header">
              <span class="orders-title">{{ $t('message.materialInOrder') }}</span>
              <el-button v-if="isAuth(670)" type="text" @click="addOrder(item)">{{ $t('message.add') }}</el-button>
            </div>
            <div class="orders-body">
              <!-- 订单行：订单号、番号、剩余点数 -->
              <div v-for="(order, orderIndex) in getOrderList(item)" :key="orderIndex" class="order-row">
                <router-link :to="`/orderPaymentDetail?order_sn=${order.order_sn}`" target="_blank" class="order-sn">
                  {{ order.order_sn }}
                </router-link>
                <span class="order-sorting">{{ order.sorting }}{{ $t('message.mash') }}</span>
                <span class="order-points">
                  {{ $t('message.remainingPoints') }}
                  <b>{{ order.send_await_num }}</b>
                  {{ $t('message.point') }}
                </span>
              </div>
              <div v-if="!getOrderList(item).length" class="order-empty">-</div>
            </div>
          </div>
        </div>
      </div>

      <!-- 分页 -->
      <div class="paginationContainer">
        <el-pagination @size-change="handleSizeChange" @current-change="handleCurrentChange"
          :current-page="searchForm.page" :page-sizes="[12, 24, 48, 96]" :page-size="searchForm.pageSize"
          layout="total, sizes, prev, pager, next, jumper" :total="total">
        </el-pagination>
      </div>
    </div>
    <material-type-setting ref="materialTypeSettingDialog" @saved="handleMaterialTypeSaved" />
    <order-bind ref="orderBindDialog" @saved="materialList" />
    <material-form ref="materialFormDialog" :type-list="typeList" @saved="handleMaterialSaved" />
    <bind-order-usage ref="bindOrderUsageDialog" @saved="materialList" />
  </div>
</template>

<script>
import MaterialTypeSetting from './components/materialTypeSetting.vue'
import OrderBind from './components/orderBind.vue'
import MaterialForm from './components/materialForm.vue'
import BindOrderUsage from './components/bindOrderUsage.vue'

export default {
  name: 'MaterialList',
  components: {
    MaterialTypeSetting,
    OrderBind,
    MaterialForm,
    BindOrderUsage
  },
  data() {
    return {
      typeList: [], // 辅料类型列表
      // 搜索条件
      searchForm: {
        order_sn: '', // 订单号筛选，与辅料列表接口字段一致
        sorting: '', // 商品管理番号
        user_id: '', // 客户名/ID
        name: '', // 辅料名称
        type_id: '', // 辅料类型
        page: 1, // 页码
        pageSize: 12 // 每页条数
      },
      total: 0, // 列表总数
      loading: false, // 列表加载状态
      tableData: [], // 辅料列表数据
      viewMode: 'list'
    }
  },
  // 初始化：加载辅料类型与列表
  created() {
    this.materialTypeList()
    this.materialList()
  },
  methods: {
    // 辅料类型列表
    materialTypeList() {
      this.$api.warehouseApi.materialTypeList().then(res => {
        this.typeList = res.data || []
      })
    },
    /*
     * 卡片字段对应：
     * 客户信息 user_id、user.realname
     * 辅料 id、type_name、type.name、name、main_image
     * 库存 material_stock_num、pre_consume_num、pre_up_num、consume
     * 所在订单 order_list[]：order_sn、sorting、remaining_points
     */
    // 辅料列表
    materialList() {
      this.loading = true
      this.$api.warehouseApi.materialList(this.searchForm).then(res => {
        const data = res.data || {}
        const list = data.list || data.data || []
        this.tableData = list
        this.total = data.total || this.tableData.length
      }).finally(() => {
        this.loading = false
      })
    },
    // 判断库存是否不足：实际库存 < 预计消耗
    isStockInsufficient(item) {
      return Number(item.material_stock_num || 0) < Number(item.pre_consume_num || 0)
    },
    // 判断是否无库存：实际库存数量为 0
    isOutOfStock(item) {
      return Number(item.material_stock_num || 0) === 0
    },
    // 格式化数量展示
    formatNum(num) {
      return num != null && num !== '' ? num : '-'
    },
    getUserName(item) {
      return (item.user && item.user.realname) || item.realname || item.user_name || '-'
    },
    getMaterialTypeName(item) {
      return item.type_name || (item.type && item.type.name) || '-'
    },
    getOrderList(item) {
      if (Array.isArray(item.detail_list)) return item.detail_list
      if (Array.isArray(item.order_list)) return item.order_list
      return []
    },
    stockStatusText(item) {
      if (this.isOutOfStock(item)) return this.$t('message.noStock')
      if (this.isStockInsufficient(item)) return this.$t('message.stockInsufficient')
      if (Number(item.pre_up_num || 0) > 0) return this.$t('message.suggestReplenishment')
      return this.$t('message.stockSufficient')
    },
    stockStatusClass(item) {
      if (this.isOutOfStock(item) || this.isStockInsufficient(item)) return 'is-danger'
      if (Number(item.pre_up_num || 0) > 0) return 'is-warning'
      return 'is-success'
    },
    // 查询
    search() {
      this.searchForm.page = 1
      this.materialList()
    },
    // 重置搜索条件
    reset() {
      this.searchForm = {
        order_sn: '',
        sorting: '',
        user_id: '',
        name: '',
        type_id: '',
        page: 1,
        pageSize: 10
      }
      this.materialList()
    },
    // 切换当前页
    handleCurrentChange(val) {
      this.searchForm.page = val
      this.materialList()
    },
    // 切换每页条数
    handleSizeChange(val) {
      this.searchForm.pageSize = val
      this.searchForm.page = 1
      this.materialList()
    },
    // 辅料类别设置
    materialTypeSetting() {
      this.$refs.materialTypeSettingDialog.open()
    },
    handleMaterialTypeSaved() {
      this.materialTypeList()
      this.materialList()
    },
    // 登记辅料
    materialAdd() {
      this.$refs.materialFormDialog.openAdd()
    },
    handleMaterialSaved() {
      this.materialTypeList()
      this.materialList()
    },
    // 辅料详细
    detail(item) {
      const route = this.$router.resolve({
        name: 'materialDetail',
        query: {
          id: item.id,
          user_id: item.user_id
        }
      })
      window.open(route.href, '_blank')
    },
    // 编辑辅料
    edit(item) {
      this.$refs.materialFormDialog.openEdit(item)
    },
    // 删除辅料
    deleteItem(item) {
      this.$confirm(this.$t('message.confirmDeleteMaterial'), this.$t('message.prompt'), {
        type: 'warning',
        center: true,
      }).then(() => {
        this.$api.warehouseApi.materialDelete({ id: item.id }).then(() => {
          this.$message.success(this.$t('message.successfullyDeleted'))
          this.materialList()
        })
      }).catch(() => { })
    },
    // 新增辅料所在订单
    addOrder(item) {
      if (!this.isAuth(670)) return
      this.$refs.orderBindDialog.open(item)
    },
    // 预计消耗数量明细：订单绑定辅料使用列表
    openBindOrderUsage(item) {
      this.$refs.bindOrderUsageDialog.open(item)
    }
  }
}
</script>

<style lang="scss" scoped>
.material-page {
  width: 100%;
  min-height: 100%;
  padding: 24px;
  box-sizing: border-box;
  background: #f8f8f8;
  color: #333;
  font-size: 14px;
}

.search-area {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 12px;
  width: 100%;
  margin-bottom: 20px;

  .item {
    flex: 1 1 180px;
    min-width: 0;
    max-width: 240px;

    .el-input,
    .el-select {
      width: 100%;
    }
  }

  .search-buttons {
    display: flex;
    gap: 10px;
  }

  .action-buttons {
    display: flex;
    flex: 1 1 auto;
    align-items: center;
    justify-content: flex-end;
    gap: 10px;

    .view-switch {
      display: flex;
      padding: 4px;
      background: #fff;
      border-radius: 6px;

      .el-button {
        width: 32px;
        height: 28px;
        padding: 0;
        border: 0;
        color: #303133;
        background: transparent;

        &+.el-button {
          margin-left: 0;
        }

        &.active {
          color: #2c96e7;
          background: #edf2f7;
        }
      }
    }
  }
}

// 列表视图外层，宽度不足时横向滚动
.material-list {
  width: 100%;
  color: #333;
  background: #fff;
  position: relative;
}

// 表头和内容行使用同一套列宽，保证上下对齐
.list-head,
.list-row {
  display: grid;
  grid-template-columns: 110px 76px minmax(230px, 1.7fr) 105px 125px 125px 100px 95px minmax(230px, 1.5fr) 92px;
  min-width: 1180px;
}

// 列表表头
.list-head {
  min-height: 42px;
  align-items: center;
  color: #333;
  background: #EDEFF4;
  font-weight: 600;
  position: sticky;
  top: 0;
  left: 0;
  z-index: 99;

  div {
    padding: 0 14px;
    box-sizing: border-box;
    text-align: center;
  }
}

// 列表内容行
.list-row {
  min-height: 150px;
  align-items: center;
  color: #333;
  border-bottom: 1px solid #dcdfe6;

  >div {
    padding: 12px 14px;
    box-sizing: border-box;
    word-break: break-all;
  }
}

// 无数据占位
.list-empty {
  padding: 40px 0;
  color: #999;
  text-align: center;
}

// 客户、ID、数量、状态列居中
.client-cell,
.id-cell,
.num-cell,
.status-cell {
  text-align: center;
}

.actual-stock-cell {
  white-space: nowrap;
  word-break: keep-all;
}

.client-cell {
  p {
    margin: 0 0 6px;

    &:last-child {
      margin-bottom: 0;
    }
  }
}

// 辅料详情列：图片、名称、类型
.material-cell {
  display: flex;
  align-items: center;
  gap: 12px;

  .material-thumb {
    flex: 0 0 60px;
    width: 60px;
    height: 60px;
    overflow: hidden;
    background: #9b9b9b;
    border-radius: 6px;
  }

  .material-text {
    min-width: 0;
  }

  .material-title {
    margin-bottom: 6px;
    color: #333;
    font-weight: 600;
    line-height: 18px;
  }
}

.inline-detail {
  margin-left: 4px;
  padding: 0;
  color: #b4272d;
}

// 库存状态标签
.stock-status {
  display: inline-block;
  min-width: 62px;
  padding: 5px 10px;
  border-radius: 3px;
  line-height: 16px;

  &.is-danger {
    color: #b4272d;
    background: #fdecec;
  }

  &.is-success {
    color: #15ad3c;
    background: #eaf8ef;
  }

  &.is-warning {
    color: #e6a23c;
    background: #fdf3e3;
  }
}

// 辅料所在订单列表
.orders-cell {
  .order-card {
    padding: 8px 10px;
    margin-bottom: 8px;
    background: #f7f7f7;
    border-radius: 4px;

    &:last-child {
      margin-bottom: 0;
    }
  }

  .order-sn {
    display: block;
    margin-bottom: 4px;
    color: #b4272d;
    font-weight: 600;
    text-decoration: underline;
  }

  .order-points {
    margin-left: 12px;

    b {
      color: #b4272d;
    }
  }
}

// 操作按钮纵向排列，保持截图中的按钮组形态
.operation-cell {
  display: flex;
  flex-direction: column;
  align-items: center;

  .el-button {
    width: 76px;
    margin: 0 0 8px !important;

    &:last-child {
      margin-bottom: 0 !important;
    }
  }
}

// 列表按钮颜色：编辑
.btn-green {
  color: #fff;
  background: #15ad3c;
  border-color: #15ad3c;

  &:hover,
  &:focus {
    color: #fff;
    background: #3cba5b;
    border-color: #3cba5b;
  }
}

// 列表按钮颜色：删除
.btn-dark {
  color: #fff;
  background: #303133;
  border-color: #303133;

  &:hover,
  &:focus {
    color: #fff;
    background: #4a4c50;
    border-color: #4a4c50;
  }
}

// 列表按钮颜色：订单绑定
.btn-blue {
  color: #fff;
  background: #2c96e7;
  border-color: #2c96e7;

  &:hover,
  &:focus {
    color: #fff;
    background: #4aa8ef;
    border-color: #4aa8ef;
  }
}

.card-list {
  display: flex;
  flex-wrap: wrap;
  gap: 20px;
  width: 100%;
}

.material-card {
  display: flex;
  flex-direction: column;
  width: 510px;
  border-radius: 6px;
  background: #fff;
  box-sizing: border-box;
}

.card-top {
  padding: 12px 18px;
}

.card-header {
  .header-line {
    display: flex;
    align-items: center;
    margin-bottom: 12px;
    line-height: 14px;

    .hl-text {
      margin-right: 18px;
      color: #333;
      word-break: break-all;
    }

    .header-icon {
      margin-left: auto;
      font-size: 16px;
      color: #B4272D;
      cursor: pointer;

    }
  }
}

.card-main {
  display: flex;
  gap: 12px;

  .material-img {
    flex: 0 0 122px;
    width: 122px;
    height: 122px;
    background: #d8d8d8;
    border-radius: 4px;
    overflow: hidden;
  }

  .material-detail {
    flex: 1;
    min-width: 0;
  }
}

.detail-title {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-bottom: 12px;

  .type-tag {
    flex: 0 0 auto;
    padding: 6px 8px;
    line-height: 12px;
    color: #2c96e7;
    background: #ebf2ff;
    border-radius: 4px;
    font-size: 12px;
  }

  .material-name {
    flex: 1;
    min-width: 0;
    color: #333;
    word-break: break-all;
  }
}

.detail-info {
  .info-row {
    margin-bottom: 12px;
    line-height: 14px;
    word-break: break-all;

    &:last-child {
      margin-bottom: 0;
    }

    .label {
      color: #666;
    }

    .value {
      color: #333;

      &.value-stock {
        color: #2c96e7;
      }

      &.value-consume {
        color: #b4272d;
      }

      &.value-replenish {
        color: #15ad3c;
      }
    }

    .detail-btn {
      margin-left: 8px;
      padding: 0;
      font-size: 14px;
    }

    .status {
      color: #15ad3c;

      &.insufficient {
        color: #b4272d;
      }
    }
  }
}

.detail-btns {
  display: flex;
  gap: 12px;
  margin-top: 16px;

  .btn-green {
    color: #fff;
    background: #15ad3c;
    border-color: #15ad3c;

    &:hover {
      background: #3cba5b;
      border-color: #3cba5b;
    }
  }
}

.card-divider {
  height: 1px;
  margin: 0 18px;
  background: #dcdfe6;
}

.card-orders {
  padding: 16px 18px;


  .orders-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 12px;

    .orders-title {
      font-size: 16px;
      font-weight: 600;
      color: #333;
    }
  }

  .orders-body {
    display: flex;
    flex-direction: column;
    gap: 15px;
    max-height: 100px;
    overflow-y: auto;
  }

  .order-row {
    display: flex;
    align-items: center;
    gap: 12px;
    line-height: 14px;

    .order-sn {
      flex: 1;
      min-width: 0;
      color: #2c96e7;
      word-break: break-all;
    }

    .order-sorting {
      flex: 0 0 auto;
      color: #333;
    }

    .order-points {
      flex: 0 0 auto;
      margin-left: auto;
      color: #666;

      b {
        margin: 0 2px;
        color: #b4272d;
      }
    }
  }

  .order-empty {
    text-align: center;
    color: #999;
  }
}

.paginationContainer {
  width: 100%;
  margin-top: 20px;

}
</style>
